import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { SCREEN_PATHS, hasScreen } from "@/constants/screens";
import { Loader2 } from "lucide-react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

// Each count opens the first screen the user may use for that stage.
const WORK_CARDS = [
  { key: "PLANNING", screens: ["planning"], tone: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/40" },
  { key: "SUBMITTED", screens: ["planning", "receiver_warehouse"], tone: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-900/40" },
  { key: "BOXING", screens: ["receiver_warehouse", "planning"], tone: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900/40" },
  { key: "BOXED", screens: ["marketplace_amazon_fba", "receiver_warehouse"], tone: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/40" },
  { key: "COMPLETED", screens: ["marketplace_amazon_fba", "receiver_warehouse", "planning"], tone: "text-slate-700 dark:text-slate-200", bg: "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800" },
];

const PHYSICAL_CARDS = [
  { key: "DRAFT", screens: ["sender_warehouse"] },
  { key: "READY", screens: ["sender_warehouse"] },
  { key: "IN_TRANSIT", screens: ["receiver_warehouse"] },
  { key: "RECEIVED", screens: ["receiver_warehouse"] },
];

const ATTENTION_CARDS = [
  { key: "open_discrepancies", tone: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40" },
  { key: "open_revisions", tone: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40" },
  { key: "unlabeled_ready_boxes", tone: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40" },
  { key: "stalled_work", tone: "text-slate-700 dark:text-slate-200", bg: "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800" },
];

const CYCLE_KEYS = ["ship_to_receive_hours", "queue_to_boxing_hours", "boxing_hours", "labelling_hours"];

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: fresh } = await axios.get(`${API}/dashboard`, ax);
      setData(fresh);
    } catch { /* keep the numbers already shown */ }
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);
  const { refresh, refreshing } = useAutoRefresh(load, { interval: 60_000 });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!data) {
    return <p className="py-12 text-center text-sm text-slate-500">{t("dash.load_error")}</p>;
  }

  const workCounts = data.work_counts || {};
  const physicalCounts = data.physical_counts;
  const attention = data.attention || {};
  const cycleTimes = data.cycle_times || {};
  const recentActivity = data.recent_activity || [];

  const openFirst = (screens) => {
    const screen = screens.find(s => hasScreen(user, s));
    return screen ? () => navigate(SCREEN_PATHS[screen]) : null;
  };

  const eventLabel = (action) => {
    const key = `dash.event.${action}`;
    const label = t(key);
    return label === key ? action : label;
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("dash.subtitle")}</p>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}>
            {t("nav.dashboard")}
          </h1>
        </div>
        <RefreshButton onClick={refresh} refreshing={refreshing} />
      </div>

      <section className="space-y-3">
        <h2 className="font-bold text-slate-900 dark:text-white text-base">{t("dash.work_title")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {WORK_CARDS.map(card => {
            const open = openFirst(card.screens);
            return (
              <button key={card.key} type="button" disabled={!open} onClick={open || undefined}
                data-testid={`dashboard-work-${card.key}`}
                className={`text-left p-4 rounded-2xl border transition-all ${card.bg} ${open ? "hover:shadow-md active:scale-[0.99]" : "cursor-default"}`}>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-tight">{t(`status.${card.key}`)}</p>
                <p className={`mt-3 text-4xl font-extrabold leading-none ${card.tone}`}>{workCounts[card.key] || 0}</p>
              </button>
            );
          })}
        </div>
      </section>

      {physicalCounts && (
        <section className="space-y-3">
          <div>
            <h2 className="font-bold text-slate-900 dark:text-white text-base">{t("dash.physical_title")}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("dash.physical_hint")}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PHYSICAL_CARDS.map(card => {
              const open = openFirst(card.screens);
              return (
                <button key={card.key} type="button" disabled={!open} onClick={open || undefined}
                  data-testid={`dashboard-physical-${card.key}`}
                  className={`text-left p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 transition-all ${open ? "hover:shadow-md" : "cursor-default"}`}>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t(`status.${card.key}`)}</p>
                  <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-white">{physicalCounts[card.key] || 0}</p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="font-bold text-slate-900 dark:text-white text-base">{t("dash.attention_title")}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("dash.attention_hint")}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {ATTENTION_CARDS.map(card => (
            <div key={card.key} data-testid={`dashboard-attention-${card.key}`} className={`rounded-2xl border p-4 ${card.bg}`}>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t(`dash.attention.${card.key}`)}</p>
              <p className={`mt-2 text-3xl font-extrabold ${card.tone}`}>{attention[card.key] || 0}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-bold text-slate-900 dark:text-white text-base">{t("dash.cycle_title")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CYCLE_KEYS.map(key => (
            <div key={key} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t(`dash.cycle.${key}`)}</p>
              <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-white">
                {cycleTimes[key] == null ? "—" : cycleTimes[key]}
              </p>
              <p className="mt-1 text-xs text-slate-500">{t("dash.avg_hours")}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <h2 className="font-bold text-slate-900 dark:text-white text-base">{t("dash.activity_title")}</h2>
        </div>
        {recentActivity.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm">{t("dash.activity_empty")}</div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-800/60">
            {recentActivity.map(event => (
              <div key={event.id} className="px-5 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{eventLabel(event.action)}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{event.actor_name || t("dash.system")}</p>
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">
                  {event.created_at ? new Date(event.created_at).toLocaleString() : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
